import { auth } from '../firebase';
import {
  offPlatformBillingBlockedMessage,
  shouldBlockOffPlatformBilling,
} from '../utils/shopifyBillingRail';

function getFunctionsBaseUrl(): string {
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || '';
  const functionsRegion = process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || 'us-central1';
  const defaultUrl = `https://${functionsRegion}-${projectId}.cloudfunctions.net`;
  const envUrl = (process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/$/, '');
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  if (isLocalhost) {
    return defaultUrl;
  }

  if (envUrl) {
    const looksLikeFunctionsHost =
      envUrl.includes('cloudfunctions.net') ||
      envUrl.includes('127.0.0.1') ||
      envUrl.includes('localhost');
    if (looksLikeFunctionsHost) {
      return envUrl;
    }
  }

  return defaultUrl;
}

export interface CreateDodoCheckoutParams {
  plan: 'pro' | 'business';
  subscribers: number;
  billingPeriod: 'monthly' | 'annual';
  customerEmail: string;
  customerName: string;
  returnOrigin: string;
}

function formatCreateCheckoutError(data: Record<string, unknown>): string {
  const base =
    (typeof data.error === 'string' && data.error) ||
    (typeof data.message === 'string' && data.message) ||
    'Failed to start Dodo checkout';
  const bits: string[] = [base];
  if (typeof data.mapKey === 'string' && data.mapKey) {
    bits.push(`Expected product map key: ${data.mapKey}`);
  }
  if (typeof data.hint === 'string' && data.hint) {
    bits.push(data.hint);
  }
  if (typeof data.productId === 'string' && data.productId) {
    bits.push(`Product ID: ${data.productId}`);
  }
  if (typeof data.dodoEnvironment === 'string' && data.dodoEnvironment) {
    bits.push(`Dodo environment: ${data.dodoEnvironment}`);
  }
  return bits.join(' ');
}

export async function createDodoCheckoutSession(
  params: CreateDodoCheckoutParams
): Promise<{ checkoutUrl: string; sessionId: string }> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('You must be signed in to continue to checkout');
  }

  if (await shouldBlockOffPlatformBilling()) {
    throw new Error(offPlatformBillingBlockedMessage());
  }
  const token = await user.getIdToken();
  const res = await fetch(`${getFunctionsBaseUrl()}/createDodoCheckout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new Error(formatCreateCheckoutError(data));
  }
  const checkoutUrl = data.checkoutUrl;
  const sessionIdOut = data.sessionId;
  if (typeof checkoutUrl !== 'string' || !checkoutUrl) {
    throw new Error('Invalid response from payment service');
  }
  return {
    checkoutUrl,
    sessionId: typeof sessionIdOut === 'string' ? sessionIdOut : '',
  };
}

export type CompleteDodoCheckoutResult = {
  ok: boolean;
  /** Firestore `invoices` doc id from Cloud Function (when written server-side). */
  invoiceId?: string | null;
  paymentId?: string | null;
  dodoSubscriptionId?: string | null;
  plan?: string;
  subscribers?: number;
  billingPeriod?: string;
  amountUsd?: number;
  source?: string;
};

export type CompleteDodoCheckoutParams = {
  sessionId?: string;
  subscriptionId?: string;
  paymentId?: string;
};

export async function completeDodoCheckout(
  params: CompleteDodoCheckoutParams | string
): Promise<CompleteDodoCheckoutResult> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('You must be signed in to complete checkout');
  }
  const body =
    typeof params === 'string'
      ? { sessionId: params }
      : {
          ...(params.sessionId ? { sessionId: params.sessionId } : {}),
          ...(params.subscriptionId ? { subscriptionId: params.subscriptionId } : {}),
          ...(params.paymentId ? { paymentId: params.paymentId } : {}),
        };
  if (!body.sessionId && !body.subscriptionId && !body.paymentId) {
    throw new Error('Nothing to complete: missing session, subscription, and payment id');
  }
  const token = await user.getIdToken();
  const res = await fetch(`${getFunctionsBaseUrl()}/completeDodoCheckout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const msg =
      (typeof data.error === 'string' && data.error) ||
      (typeof data.message === 'string' && data.message) ||
      (res.status === 429 ? 'Payment service is busy. Please wait a moment.' : 'Failed to complete checkout');
    const err = new Error(msg) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return data as CompleteDodoCheckoutResult;
}
